/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import { UserInterface } from '../../../models/interface/user.interface';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { catchError, distinctUntilChanged, filter, mergeMap, skip, take } from 'rxjs/operators';
import { faStar } from '@fortawesome/free-regular-svg-icons/faStar';
import { Store } from '@ngrx/store';
import { InterventionSharedService } from '../../../services/shared/intervention-shared/intervention-shared.service';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { UserActionTypes } from 'src/app/store/user/user.action';

/**
 * Component:
 * Collaborator page displaying a list of collaborators of the group;
 * Can be found: {uri}/interventions/{{intervention_id}}/collaborators
 */

@Component({
  selector: 'app-collaborator',
  templateUrl: './intervention-collaborator.component.html',
  styleUrls: ['./intervention-collaborator.component.scss']
})
export class InterventionCollaboratorComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator;

  // Icons
  faSearch = faSearch;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faStar = faStar;
  faEnvelope = faEnvelope;

  // Filter
  public searchFilter = '';
  public isLoading$: Observable<boolean>;

  // List of roles
  public roles = ['study.ecoachmanager', 'study.ecoach', 'study.access'];

  public myUserId: number;

  // Data provided by StudyService
  public collaborators: Array<UserInterface> = [];

  // Data provided by UserService
  public eCoaches: Array<UserInterface> = [];

  public isManager$: Observable<boolean>;
  public isCollapse$: Observable<boolean>;
  public collaborators$: Observable<Array<UserInterface>>;

  public pagedCollaborators: Array<UserInterface>;
  public pagedCollaborators$: Observable<Array<UserInterface>>;

  // Translation
  public param = { intervention_name: '...' };
  public profile$: Observable<UserInterface>;
  public isCollapseSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private studyId: number;

  private searchTextSubject = new BehaviorSubject<string>('');

  private isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private isManagerSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private collaboratorsSubject: BehaviorSubject<Array<UserInterface>> = new BehaviorSubject([]);
  private pagedCollaboratorsSubject: BehaviorSubject<Array<UserInterface>> = new BehaviorSubject<Array<UserInterface>>([]);

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;
  private allCollaborators$: Observable<Array<UserInterface>>;

  private subscriptions: Subscription[] = [];

  constructor(
    private sharedService: InterventionSharedService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private store: Store<{
      myProfile: ProfileInterface;
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
      allECoaches: UserInterface[];
    }>
  ) {
    this.profile$ = store.select('myProfile');
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.collaborators$ = this.collaboratorsSubject.asObservable();
    this.pagedCollaborators$ = this.pagedCollaboratorsSubject.asObservable();
    this.allCollaborators$ = store.select('allECoaches');
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    // Sharing parent values: id and study name
    this.subscriptions.push(
      this.sharedService.intervention$
        .pipe(
          filter((intervention: InterventionInterface) => intervention !== null),
          mergeMap((intervention: InterventionInterface) => {
            this.param = { intervention_name: intervention.attributes.name };
            this.studyId = intervention.attributes.study_id;
            return this.profile$.pipe(
              filter((user: UserInterface) => user !== null),
              take(1)
            );
          })
        )
        .subscribe(
          (result: UserInterface) => {
            this.myUserId = result.id;
            this.applyCollaboratorFilter();
          },
          error => {
            this.applyCollaboratorFilter();
            throwError(error);
          }
        )
    );
  }

  public applyCollaboratorFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadCollaboratorList(true).subscribe((result: any) => {
        this.isLoadingSubject.next(false);
      })
    );
  }

  public reloadCollaboratorList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
    const reqs: Array<Observable<any>> = [];
    this.store.dispatch({ type: StudyActionTypes.getCollaboratorsType, payload: { studyId: this.studyId, include: 'roles' } });
    this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.studyId));
    reqs.push(
      this.collaboratorsOfStudy$.pipe(
        filter(collaboratorsOfStudy => !!collaboratorsOfStudy),
        take(1)
      )
    );
    this.store.dispatch({ type: UserActionTypes.getAllEcoachesType, payload: {} });
    reqs.push(this.allCollaborators$.pipe(skip(1), take(1)));

    return forkJoin(reqs).pipe(
      mergeMap((results: Array<any>) => {
        // Collaborators of the study
        this.collaborators = results[0].collaborators;

        // All user with role editor
        this.eCoaches = this.getECoachesNotInStudy(results[1], this.collaborators);

        // Determine my highest role
        const myself: UserInterface = this.collaborators.find(user => user.id === this.myUserId);
        if (myself !== undefined) {
          const myRoleSlug = this.helper.getHighestRoleOfCollaborator(myself);
          if (myRoleSlug.match(/study\.(ecoachmanager|owner)$/)) {
            this.isManagerSubject.next(true);
          }
        }

        // If user is not manager - they can only see study.ecoachmanager and study.owner
        if (!this.isManagerSubject.value) {
          this.collaborators = this.collaborators.filter(
            (user: UserInterface) =>
              this.helperService.hasRoles(user.relationships?.roles?.data, /study\.(ecoachmanager|owner)$/) ||
              user.id.toString() === this.myUserId.toString()
          );
        }

        // Enable search and pagination
        return this.search(this.searchFilter).pipe(
          mergeMap(() =>
            this.helper.setPagedContent(this.collaboratorsSubject, this.pagedCollaboratorsSubject, setFirst, pageIndex, pageSize)
          )
        );
      }),
      catchError(error => throwError('ForkJoin error', error))
    );
  }

  public updateList(): void {
    this.subscriptions.push(this.reloadCollaboratorList().subscribe());
  }

  public updatePagedCollaborators(event: any) {
    if (event) {
      this.pagedCollaboratorsSubject.next(event);
    }
  }

  public openDialogGroupCollaboratorAdd(user?: UserInterface): void {
    this.helperDialog
      .openDialogGroupCollaboratorAdd(
        this.param,
        this.studyId,
        this.collaborators.filter(collaborator => collaborator.attributes.is_verified === 1),
        this.eCoaches
      )
      .afterClosed()
      .pipe(mergeMap((value: any) => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  public openDialogGroupCollaboratorEdit(user?: UserInterface): void {
    this.helperDialog
      .openDialogGroupCollaboratorEdit(
        this.param,
        this.studyId,
        this.collaborators.filter(collaborator => collaborator.attributes.is_verified === 1),
        this.eCoaches,
        user
      )
      .afterClosed()
      .pipe(mergeMap((value: any) => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  public openDialogGroupCollaboratorRemove(user?: UserInterface): void {
    this.helperDialog
      .openDialogGroupCollaboratorRemove(
        this.param,
        this.studyId,
        this.collaborators.filter(collaborator => collaborator.attributes.is_verified === 1),
        this.eCoaches,
        user
      )
      .afterClosed()
      .pipe(mergeMap((value: any) => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchTextSubject.next(name);
    return this.searchTextSubject.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        this.collaboratorsSubject.next(this.helper.filterUsersBy(this.collaborators, this.searchFilter.toLowerCase().trim()));
        return of(true);
      })
    );
  }

  // Get all eCoaches who are not part of the study/study
  private getECoachesNotInStudy(allECoaches: Array<UserInterface>, myCollaborators: Array<UserInterface>): Array<UserInterface> {
    return allECoaches.filter(eCoach => !myCollaborators.map((user: UserInterface) => user.id).includes(eCoach.id));
  }
}
